import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import SearchPackages from '../Search/SearchPackages';
import { getPackages } from 'services/package';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SearchPackageModel } from 'ts/package/classes/SearchPackageModel';
import { PackageSearch, PackagesResponse } from 'ts/package/interfaces/Package';
import PackagesList from './PackagesList';
import ResultTopBar from './ResultTopBar';
import { getFilteredPackages, getSortedPackages } from 'utils/package';
import PackageFilter from './PackageFilter';
import { PackageFilterModel } from 'ts/package/classes/PackageFilterModel';
import { getDaysDifference, openInNewWindow } from 'utils/utils';
import PackagesResultSkeleton from './PackagesResultSkeleton';
import ExpiredTimeout from 'components/shared/ExpiredTimeout';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import * as idb from 'utils/shared/idbUrl';
import { useGetPage } from 'hooks/useGetPage';
import useRecaptcha from 'hooks/useRecaptcha';
import { useTranslation } from 'react-i18next';

import usePackageDataLayer from 'hooks/usePackageDataLayer';
import { MainContext } from 'contexts/MainContext';
import { b2b, isNetravel } from 'constants/generic';

const ResultPackagePage = () => {
  const [urlParams] = useSearchParams();
  const [response, setResponse] = useState<PackagesResponse>();
  const [packages, setPackages] = useState<PackageSearch[]>([]);
  const [filter, setFilter] = useState<PackageFilterModel>();
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const showModalTimeoutRef = useRef<any>();
  const daysDiff = useRef(0);
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const { t, i18n } = useTranslation();
  const isFirstLoad = useRef(true);
  const { userSiteConfig } = useContext(MainContext);
  const navigate = useNavigate();
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  const [sort, setSort] = useState('');
  const { pushImpression } = usePackageDataLayer();
  useGetPage('ILPackages/Search');
  usePageInMemory(idbKey, () => saveState());

  const stateRef = useRef<any>();
  stateRef.current = { packages, filter, response, sort };

  useEffect(() => {
    if (b2b) {
      if (location.pathname.toLowerCase() !== `/ilpackages/results`) {
        return;
      }

      const latestSearch = sessionStorage.getItem('latestPacPath');
      if (latestSearch) {
        const latest = JSON.parse(latestSearch);

        const latestSearchObj = new URLSearchParams(latest.search);
        const locationSearchObj = new URLSearchParams(location.search);

        if (latestSearchObj.toString() === locationSearchObj.toString() && !isFirstLoad.current) {
          return;
        }
      }
    }

    (async () => {
      const savedState = await idb.getPageState(idbKey);

      const needToSearch = urlParams.get('needToSearch');
      if (needToSearch === 'false') {
        const element = document.querySelector('.search-wrapper') as HTMLElement;
        element?.classList.add('visible-responsive');
        setResponse(undefined);
        setPackages([]);
        setFilter(undefined);
        return;
      }
      if (savedState && savedState.response) {
        setPackages(savedState.packages);
        setFilter(savedState.filter);
        setResponse(savedState.response);
        setSort(savedState.sort);

        idb.deletePageState(idbKey);

        if (isNetravel) {
          const netravelSearchParams = sessionStorage.getItem('netravelSearchParams');
          if (netravelSearchParams) {
            const netravelSearchParamsObj = JSON.parse(netravelSearchParams);
            const pacLength = savedState.packages.length;
            const allPacLength = savedState.response?.deals.length;

            sessionStorage.setItem(
              'netravelSearchParams',
              JSON.stringify({ ...netravelSearchParamsObj, pacLength, allPacLength })
            );
          }
        }
      } else if (isRecaptchaReady) {
        setResponse(undefined);
        setPackages([]);
        setFilter(undefined);
        const recaptchaToken = await handleRecapcha();

        isFirstLoad.current = false;
        if (!recaptchaToken) return;

        daysDiff.current = getDaysDifference(urlParams.get('checkIn')!, urlParams.get('checkOut')!);
        const params = new SearchPackageModel(urlParams, recaptchaToken);
        const newResponse = await getPackages(params);
        const newPackages = getSortedPackages(
          newResponse?.deals || [],
          sessionStorage.getItem('packageSort') || 'priceIncrease'
        );
        const newFilter = new PackageFilterModel(newPackages, daysDiff.current, isNetravel);
        const filteredPackages = getFilteredPackages(newFilter, newPackages);

        setPackages(filteredPackages);
        setResponse(newResponse);
        setFilter(newFilter);
        pushImpression(newResponse?.deals || []);

        if (isNetravel) {
          const netravelSearchParams = sessionStorage.getItem('netravelSearchParams');
          if (netravelSearchParams) {
            const netravelSearchParamsObj = JSON.parse(netravelSearchParams);
            const pacLength = filteredPackages.filter((pac) => pac.visible).length;
            const allPacLength = newResponse?.deals.length;

            sessionStorage.setItem(
              'netravelSearchParams',
              JSON.stringify({ ...netravelSearchParamsObj, pacLength, allPacLength })
            );
          }
        }
      }
    })();
    clearTimeout(showModalTimeoutRef.current);
    showModalTimeoutRef.current = setTimeout(() => {
      setShowExpiredModal(true);
    }, 20 * 60 * 1000);
  }, [urlParams, isRecaptchaReady, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSortChange = (value: string) => {
    const sortedPackages = [...getSortedPackages(response?.deals || [], value)];
    setPackages(sortedPackages);
    setSort(value);
  };

  const onClickResetFilter = () => {
    const newFilter = new PackageFilterModel(packages, daysDiff.current, isNetravel);

    setFilter(newFilter);
    setPackages(getFilteredPackages(newFilter, packages));
  };

  const onClickChoose = (pac: PackageSearch) => {
    const params = Object.fromEntries(urlParams);
    const data = {
      checkIn: params.checkIn,
      cityCode: params.cityCode.split('-')[0],
      dealId: pac.dealId,
      hotelId: pac.hotelId,
      type: params.type || '1',
      provider: pac.provider,
      rowId: pac.rowId,
      isSale: 'false',
      paxComplect: pac.paxComplect,
      name: pac.rowName,
      searchToken: sessionStorage.getItem('searchPackagesToken') || '',
      discountId: pac.discountId + '',
    };

    if (userSiteConfig.base.openDetailsInNewTab) {
      openInNewWindow('/ilpackage/details?' + new URLSearchParams(data).toString());
    } else {
      saveState();
      navigate({ pathname: '/ilpackage/details', search: new URLSearchParams(data).toString() });
    }
  };

  const onChangeFilter = (newFilter: PackageFilterModel) => {
    const newPackages = getFilteredPackages(newFilter, packages);

    setFilter(newFilter);
    setPackages(newPackages);
  };

  const saveState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  const noneedToSearch = urlParams.get('needToSearch') !== 'false';
  return (
    <div className="results-main">
      <SearchPackages />
      {noneedToSearch && (
        <ResultTopBar
          onSortChange={onSortChange}
          filter={filter}
          onChangeFilter={onChangeFilter}
          sessionKey={'packageSort'}
          newSort={sort}
        />
      )}
      {!!packages.length && (
        <div className="packages-result-wr results-wr">
          {!!filter && (
            <Fragment>
              <PackageFilter
                filter={filter}
                onChangeFilter={onChangeFilter}
                onClickReset={onClickResetFilter}
              />
              <PackagesList
                packages={packages.filter((pac) => pac.visible)}
                onClickChoose={onClickChoose}
              />
            </Fragment>
          )}
        </div>
      )}
      {!response && noneedToSearch && <PackagesResultSkeleton />}
      {showExpiredModal && (
        <ExpiredTimeout navigatePath="/ILPackages" onClose={() => setShowExpiredModal(false)} />
      )}
      {!!response && !response.deals.length && <div className="no-entries">{t('no_entries')}</div>}
    </div>
  );
};

export default withErrorBoundary(ResultPackagePage, 'ResultPackagePage');
